import { EMkStepStatus, IMkCheckboxField, IMkInputField, IMkStep, TMkStep } from '@typescript'
import type { IMkBookingError, IMkBookInputField } from '@book/types'
import type { IResort } from '@resort/types'

/* ==== Waitlist create types ==== */
export enum EWaitlistStepName {
  WAITLIST_RESORT = 'waitlist-resort',
  WAITLIST_DATES = 'waitlist-dates',
  WAITLIST_SUITES = 'waitlist-suites',
  WAITLIST_CONTACT = 'waitlist-contact',
}

export interface IWaitlistStep extends IMkStep {
  fieldSet: {
    [key: string]: IMkBookInputField
  }
  formState: {
    [key: string]: number | string
  }
  key: string
  name: EWaitlistStepName | string
  status: string | EMkStepStatus
  title: string
}

export type TWaitlistStep = IWaitlistStep

export interface IWaitlistDraftMeta {
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  reservationId: string
  status: string
}

export interface IWaitlistDraft {
  errors: Array<IMkBookingError>
  meta: IWaitlistDraftMeta
  rules: {
    maxStay: string
    minStay: string
  }
  steps: IWaitlistStep[]
}

export interface IWaitlistStepApiRequest {
  completedStepName: string
  draft: IWaitlistDraft
}

export interface IWaitlistState {
  waitlistDraft: IWaitlistDraft | undefined
  isLoading: boolean
  waitlistErrors: IMkBookingError[]
}

export enum EWaitlistAction {
  SET_WAITLIST_DRAFT = 'waitlist/SET_WAITLIST_DRAFT',
  CLEAR_WAITLIST_DRAFT = 'waitlist/CLEAR_WAITLIST_DRAFT',
  UPDATE_FIELD = 'waitlist/UPDATE_FIELD',
  SET_LOADING = 'waitlist/SET_LOADING',
  SET_ERROR = 'waitlist/SET_ERROR',
  CLEAR_ERROR = 'waitlist/CLEAR_ERROR',
}

interface SetWaitlistDraftAction {
  type: typeof EWaitlistAction.SET_WAITLIST_DRAFT
  payload: IWaitlistDraft
}

interface UpdateWaitlistFieldAction {
  type: typeof EWaitlistAction.UPDATE_FIELD
  payload: {
    stepName: EWaitlistStepName | string
    fieldName: string
    fieldValue: IMkInputField['value']
    fieldType: string
  }
}

interface SetWaitlistLoadingAction {
  type: typeof EWaitlistAction.SET_LOADING
  payload: boolean
}

interface SetWaitlistErrorAction {
  type: typeof EWaitlistAction.SET_ERROR
  payload: IMkBookingError
}

interface ClearWaitlistErrorAction {
  type: typeof EWaitlistAction.CLEAR_ERROR
}

interface ClearWaitlistDraftAction {
  type: typeof EWaitlistAction.CLEAR_WAITLIST_DRAFT
}

export type WaitlistAction = SetWaitlistDraftAction | ClearWaitlistDraftAction | UpdateWaitlistFieldAction | SetWaitlistLoadingAction | SetWaitlistErrorAction | ClearWaitlistErrorAction

/* ==== Owner Interfaces ==== */
export interface IOwnerPhone {
  phoneId?: string
  phonePk?: string
  countryCode: string
  phoneNumber: string
}

export interface IOwner {
  addressId: string
  addressPk: string
  addressLine1: string
  addressLine2?: string
  addressLine3?: string
  city: string
  country: string
  emailAddress?: string
  emailPk?: string
  passwordMask: string
  phoneHome?: IOwnerPhone
  phoneMobile?: IOwnerPhone
  phoneWork?: IOwnerPhone
  postalCode: string
  stateProvince: string
  id: string
  name: string
}

export interface IDuesSummaryData {
  amountDue: string
  dues: string
  feesInterest: string
  nextDueDate: string
  pastDue: string
  payments: string
}

export interface ILoansSummaryData {
  data: {
    amountDue: string
    feesInterest: string
    loans: string
    nextDueDate: string
    pastDue: string
    payments: string
    totalDue: string
  }[]
  principalBalance: string
}

export interface ISpecialAssessmentsData {
  assessment: string
  paidAmount: string
  dueDate: string
  pastDue: string
  amountDue: string
}

export interface IDuesLoansDataAPIResponse {
  warnings: string[]
  duesSummary: IDuesSummaryData[]
  loansSummary: ILoansSummaryData
  specialAssessments: ISpecialAssessmentsData[]
}

export interface IDuesLoansContentAPIResponse {
  bottomSection: {
    children: {
      children: {
        children: string[]
        title: string
        content: string
      }[]
      title: string
    }[]
    title: string
  }
  resourceKey: string
  kind: string
  warnings: {
    value: string
    key: string
  }[]
  topSection: {
    children: string[]
    title: string
    content: string
  }
}

export interface IDuesAndLoansContent {
  title: string
  summary: string
  warnings: string[]
}

/* ==== Enums ==== */
export enum EMkDepositRCIStepName {
  SELECT_DEPOSIT = 'selectDeposit',
  COMPLETE_DEPOSIT = 'completeDeposit',
}

export enum EMkRCIDepositsAction {
  FETCH_DRAFT = 'rciDeposits/FETCH_DRAFT',
  SET_DRAFT = 'rciDeposits/SET_DRAFT',
  SET_CURRENT_STEP = 'rciDeposits/SET_CURRENT_STEP',
  UPDATE_FIELD = 'rciDeposits/UPDATE_FIELD',
  SET_ERROR = 'rciDeposits/SET_ERROR',
  CLEAR_ERROR = 'rciDeposits/CLEAR_ERROR',
  SET_LOADING = 'rciDeposits/SET_LOADING',
}

/* ==== Interfaces ==== */
export interface IMkAccountInputField extends IMkInputField {
  help?: string
  max?: number
  min?: number
  useForSum?: boolean
}

export interface IMkRCIDepositsSelectDepositStep extends IMkStep {}

export interface IMkRCIDepositsDraft {
  steps: TMkStep[]
}

export interface IMkRCIDepositsError extends Error {
  code?: string
  raw?: Error
  message: string
}

export interface IMkAccountState {
  rciDepositsDraft: IMkRCIDepositsDraft | undefined
  rciDepositsErrors: IMkRCIDepositsError[]
  isLoading: boolean
}

interface SetRCIDepositsDraftAction {
  type: typeof EMkRCIDepositsAction.SET_DRAFT
  payload: IMkRCIDepositsDraft
}

interface UpdateFieldAction {
  type: typeof EMkRCIDepositsAction.UPDATE_FIELD
  payload: {
    stepName: EMkDepositRCIStepName
    fieldName: string
    fieldValue: IMkAccountInputField['value']
    fieldType: string
  }
}

interface SetErrorAction {
  type: typeof EMkRCIDepositsAction.SET_ERROR
  payload: IMkRCIDepositsError
}

interface ClearErrorAction {
  type: typeof EMkRCIDepositsAction.CLEAR_ERROR
}

interface SetLoadingAction {
  type: typeof EMkRCIDepositsAction.SET_LOADING
  payload: boolean
}

export interface IUpcomingTrip {
  cancelEnabled: boolean
  confirmationNumber: string
  daysLeft: number
  modifyEnabled: boolean
  period: string
  resHistoryToken: string
  reservationId: string
  reservationItemId: string
  reservationToken: string
  resortImageUrl: string
  secondaryResortImageUrl: string
  /** Trips-card carousel contract from BE as multi-image payload.
   * We should make sure that it matches with actual BE response before using it.
   */
  resortImages?: Array<{
    url: string
    alt?: string
    type?: string
    thumbnailUrl?: string
  }>
  resortLocation: string
  resortName: string
  showDaysLeft: boolean
  startDate: string
  suiteType: string
  timeshareOwnerPriority?: boolean
  travelerName: string
}

export interface ITripContent {
  periodLabel: string
  suiteTypeLabel: string
  travelerNameLabel: string
  inLabel: string
  dayLabel: string
  daysLabel: string
  timeshareOwnerPriorityLabel: string
  confirmationNumberLabel: string
  modifyButton: string
  modifyButtonIcon: string
  cancelButton: string
  cancelButtonIcon: string
}

/** Past trip row under each use-year group from GET /trips */
export interface IPastTripResortImage {
  alt: string
  url: string | null
}

export interface IPastTripResort {
  image: IPastTripResortImage
  name: string
}

export interface IPastTripRow {
  confirmationNumber: string
  checkInDate: string
  checkOutDate: string
  resort: IPastTripResort
}

export interface IPastTripsYearGroup {
  useYearId: string
  useYearLabel: string
  useYearTrips: IPastTripRow[]
}

export interface IUpcomingTripsPagination {
  pageNum: number
  pageSize: number
  totalItems: number
  totalPages: number
}

export interface ITripsResponse {
  noTripsContent?: string
  upcomingTrips?: IUpcomingTrip[] | null
  pastTrips?: IPastTripsYearGroup[] | null
  totalUpcomingTrips?: number
  totalPastTrips?: number
  tripsContent: {
    tripsDescription: string
  }
  tripContent: ITripContent
  warnings?: string[]
  upcomingTripsPagination?: IUpcomingTripsPagination
}

/* ==== Waitlist (load) types ==== */
export type TWaitlistItem = {
  requestedCheckInDate: string
  requestedCheckOutDate: string
  resort: Pick<IResort, 'address' | 'images' | 'name' | 'productHubId' | 'resortNumber'>
  offer?: {
    bookBy: string
    madeOn: string
    suite: string
    offerCheckInDate: string
    offerCheckOutDate: string
  }
  waitlistId: string
  waitlistLevel: string
}

export type TWaitlistLoadResponse = {
  maxRequests: number
  waitlist: TWaitlistItem[]
}

/* ==== Types ==== */
export type IMkAnyAccountField = IMkAccountInputField | IMkCheckboxField

export type TMkRCIDepositsAction = SetRCIDepositsDraftAction | UpdateFieldAction | SetErrorAction | ClearErrorAction | SetLoadingAction
